import React from 'react';
import {
  PortableText,
  PortableTextReactComponents,
  PortableTextBlockComponent,
  PortableTextListComponent,
} from '@portabletext/react';
import type {
  PortableTextBlock,
  PortableTextListItemBlock,
} from '@portabletext/types';
import { GetInternalLinkFunctionType } from '@repo/models/GetInternalLinkFunctionType';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import BlockBuilderImage from './types/BlockBuilderImage';
import BlockBuilderLinkGrid from './types/BlockBuilderLinkGrid/BlockBuilderLinkGrid';
import MediaGrid from './types/MediaGrid';
import VideoGallery from './types/VideoGallery';
import BlockBuilderTestimonial from './types/BlockBuilderTestimonial';
import BlockBuilderTestimonialCarousel from './types/BlockBuilderTestimonialCarousel';

type SanityBlock = PortableTextBlock | PortableTextListItemBlock;

interface BlockContentProps {
  blocks: SanityBlock[];
  getInternalLink?: GetInternalLinkFunctionType;
}

const inlineIcon = ({ value }) => (
  <img
    src={value?.icon?.asset?.url}
    width={value?.iconWidth ?? '16'}
    height={value?.iconHeight ?? '16'}
    alt="Inline Icon"
    style={{ marginRight: '.25rem' }}
  />
);

const NormalBlock: PortableTextBlockComponent = ({ children }) => (
  <p>{children}</p>
);

const BlockquoteBlock: PortableTextBlockComponent = ({ children }) => (
  <blockquote>{children}</blockquote>
);

const H1Block: PortableTextBlockComponent = ({ children }) => (
  <h1>{children}</h1>
);
const H2Block: PortableTextBlockComponent = ({ children }) => (
  <h2>{children}</h2>
);
const H3Block: PortableTextBlockComponent = ({ children }) => (
  <h3>{children}</h3>
);
const H4Block: PortableTextBlockComponent = ({ children }) => (
  <h4>{children}</h4>
);
const H5Block: PortableTextBlockComponent = ({ children }) => (
  <h5>{children}</h5>
);
const H6Block: PortableTextBlockComponent = ({ children }) => (
  <h6>{children}</h6>
);

const BulletBlock: PortableTextListComponent = ({ children }) => (
  <ul className="list-inside">{children}</ul>
);

const NumberBlock: PortableTextListComponent = ({ children }) => (
  <ol className="list-inside">{children}</ol>
);

const BlockContent: React.FC<BlockContentProps> = ({
  blocks,
  getInternalLink = () => '',
}) => {
  const internalLink = ({ value, children }) => {
    const href = getInternalLink(value) ?? '/';
    return <a href={href}>{children}</a>;
  };

  const link = ({ children, value }) => {
    if (value?.linkType === 'internal') {
      const href =
        getInternalLink({ ...value, reference: value?.internalLink }) ?? '/';
      return (
        <LocalizedLink to={href}>{children ?? value?.caption}</LocalizedLink>
      );
    }
    return value?.blank ? (
      <a href={value?.href} target="_blank" rel="noreferrer">
        {children}
      </a>
    ) : (
      <a href={value?.href}>{children}</a>
    );
  };

  const mediaGrid = ({ value, getInternalLink }) => (
    <MediaGrid value={value} getInternalLink={getInternalLink} />
  );

  const components: Partial<PortableTextReactComponents> = {
    types: {
      blockBuilderTestimonial: BlockBuilderTestimonial,
      blockBuilderTestimonialCarousel: BlockBuilderTestimonialCarousel,
      blockContentImage: BlockBuilderImage,
      blockContentLinkGrid: ({ value }) =>
        BlockBuilderLinkGrid({ value, getInternalLink }),
      mediaGrid: ({ value }) => mediaGrid({ value, getInternalLink }),
      videoGallery: VideoGallery,
      inlineIcon: ({ value }) => inlineIcon({ value }),
    },
    marks: {
      internalLink: ({ value, children }) => internalLink({ value, children }),
      link: ({ children, value }) => link({ children, value }),
    },
    block: {
      normal: NormalBlock,
      blockquote: BlockquoteBlock,
      h1: H1Block,
      h2: H2Block,
      h3: H3Block,
      h4: H4Block,
      h5: H5Block,
      h6: H6Block,
    },
    list: {
      bullet: BulletBlock,
      number: NumberBlock,
    },
  };

  return <PortableText value={blocks} components={components} />;
};

export default BlockContent;
